import {merge} from 'lodash';
import Assessment from '../assessment';
import AssessmentResult from '../../../values/AssessmentResult';
import {FAQs_ASSESSMENT_ID, FAQS_POINTS} from '@axyseo/const/analysis';

/**
 * Represents an assessment that checks FAQs content and structure for SGE optimization.
 */
export default class FAQsAssessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {Object} [config] The configuration to use.
   *
   * @returns {void}
   */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: FAQs_ASSESSMENT_ID,
      ctaType: 'fix',
      docUrl: 'https://blog.google/products/search/generative-ai-search/',
      priority: 'high',
      fixPosition: 'faqs',
      title: 'FAQs Optimization',
      content: {
        good: 'FAQs are well-structured and optimized for Google SGE.',
        improve: '',
        bad: 'FAQs need significant improvement to meet Google SGE standards.',
        details: {
          hasProperStructure: 'Empty questions or answers',
          hasMinimumFAQs: 'Less than 3 FAQs'
        }
      }
    };

    this.identifier = FAQs_ASSESSMENT_ID;
    this._config = merge(defaultConfig, config);
  }

  /**
   * Executes the Assessment and returns a result.
   *
   * @param {Paper}       paper       The Paper object to assess.
   * @param {Researcher}  researcher  The Researcher object containing all available researches.
   *
   * @returns {AssessmentResult} The result of the assessment, containing both a score and a descriptive text.
   */
  getResult({paper, researcher}) {
    const calculatedResult = this.calculateResult(paper);

    const assessmentResult = new AssessmentResult({config: this._config});
    assessmentResult.setScore(calculatedResult.score);
    assessmentResult.setStatus(calculatedResult.status);
    assessmentResult.setData(calculatedResult.data);

    return assessmentResult;
  }

  /**
   *
   * @param paper
   * @returns {{score: number, status: string, data: {hasSchema: ((function(): *)|boolean), hasProperStructure: boolean, hasMinimumFAQs: boolean}}|{score: number, status: string, data: {hasFAQs: boolean}}}
   */
  calculateResult(paper) {
    let status = 'bad';
    const faqs = paper.getFAQs()?.questions || [];
    let score = 0;

    if (!faqs || faqs.length === 0) {
      return {score: 0, status: 'bad', data: {hasFAQs: false}};
    }
    const isEnableFaqs = paper.getFAQs()?.status;
    const hasMinimumFAQs = this.checkMinimumFAQs(faqs);
    status = hasMinimumFAQs && isEnableFaqs ? 'good' : 'bad';
    score = this.getScore(FAQS_POINTS, status);

    return {
      score,
      status
    };
  }



  /**
   * Checks if there are at least 3 FAQs
   * @param {Array} faqs
   * @returns {boolean}
   */
  checkMinimumFAQs(faqs) {
    return faqs.length >= 3;
  }

  /**
   * Checks whether the paper has text.
   *
   * @param {Paper}       paper       The paper to use for the assessment.
   *
   * @returns {boolean} True when there is text.
   */
  isApplicable(paper) {
    return true;
  }
}
